import cx from 'classnames';
import { CSSProperties, useCallback, useEffect, useRef } from 'react';
import root from 'react-shadow';

import { registerCustomElements } from './customElements';
import { articleCssCDN } from './utils';

const articleRenderWaitTime = 500;

type ArticleBodyProps = {
  articleId: string;
  articleCollectionId: string;
  html: string;
  cssVersion?: string;
  onClickInterlink?: ({
    articleId,
    articleCollectionId,
  }: {
    articleId: string;
    articleCollectionId: string;
  }) => void;
  className?: string;
  outerClassName?: string;
  outerStyle?: CSSProperties;
  withCSS?: boolean;
};
function ArticleBodyInternal({
  articleId,
  articleCollectionId,
  html,
  className,
  onClickInterlink,
  withCSS = true,
}: ArticleBodyProps): JSX.Element {
  const articleRef = useRef<HTMLDivElement>(null);

  const onArticleClick = (e: React.MouseEvent<HTMLElement>) => {
    const target = e.target as HTMLElement;

    if (openImageAtFullSize(target)) {
      e.preventDefault();
      return;
    }

    // Marks (bold, italic, …) nest elements inside the interlink anchor, so
    // the click target may be a descendant of the element carrying the
    // attribute rather than the element itself.
    const knowledgeArticle = target
      .closest('[data-knowledge-article]')
      ?.getAttribute('data-knowledge-article');
    if (knowledgeArticle) {
      e.preventDefault();
      e.stopPropagation();
      onClickInterlink &&
        onClickInterlink({ articleId: knowledgeArticle, articleCollectionId });
      return;
    }
    handleAnchorClick(articleRef.current as HTMLDivElement, e);
  };

  /**
   * Need to wrap the article with this in order to get anchor scrolling working
   * for interlinked articles
   */
  const interlinkingArticleRef = useCallback(
    (node: HTMLElement | null) => {
      /**
       * Need a setTimeout to give the UI time to render/apply styles to the
       * article before the scroll is invoked. If you call scrollIntoView
       * immediately, the final scroll position may be incorrect for slower
       * browsers.
       */
      setTimeout(() => {
        // Check if the article id has an anchor in it.
        const isLinkedArticle = articleId.match('#');
        if (node === null || !isLinkedArticle) return;
        const articleAnchor = articleId.split('#')[1] || '';
        const articleLink: HTMLElement | null =
          node.querySelector(`[id="${articleAnchor}"]`) ||
          node.querySelector(`[name="${articleAnchor}"]`);

        if (articleLink) {
          articleLink.scrollIntoView({ behavior: 'smooth' });
        }
      }, articleRenderWaitTime);

      return;
    },
    [articleId],
  );

  // Register web components
  useEffect(() => {
    registerCustomElements();
  }, []);

  // Originally required to render Knowledge content. Content is pre-sanitized by Knowledge backend.
  return (
    <>
      {withCSS && (
        <link
          rel="stylesheet"
          type="text/css"
          href={articleCssCDN({ noREMs: true })}
        />
      )}

      <div ref={interlinkingArticleRef}>
        <div
          className={cx(`article-body`, className, `article`)}
          style={{ padding: 0, marginBottom: 0 }} // Required to override default article.min.css padding styles
          dangerouslySetInnerHTML={{ __html: html }}
          ref={articleRef}
          onClick={onArticleClick}
        />
      </div>
    </>
  );
}

/**
 * Makes the article body `ref` work in the correct order.
 *
 * If it's all one component, then initially the `ref` will be `undefined`.
 */
export function ArticleBody(props: ArticleBodyProps): JSX.Element {
  return (
    <root.div
      className={cx('article-container', props.outerClassName)}
      style={props.outerStyle}
    >
      <ArticleBodyInternal {...props} />
    </root.div>
  );
}

// Check if a anchor tag has been clicked on.
// If so check if we should scroll
// Otherwise open the link in a new tab.

/**
 * Opens the picture behind a clicked image, which an article shows at the width
 * of its column however much detail the picture holds.
 *
 * Answers false when the click was not on one, so the caller can carry on.
 */
function openImageAtFullSize(target: HTMLElement): boolean {
  const image = target.closest<HTMLImageElement>(
    'img.image-preview, img.image-inline',
  );
  // An image that is itself a link keeps its link.
  if (!image || target.closest('a')) return false;

  const source = image.currentSrc || image.src;
  if (!source) return false;

  const url = new URL(source);
  url.searchParams.delete('width');

  window.open(url.href, '_blank', 'noopener');

  return true;
}

function handleAnchorClick(
  articleElement: HTMLDivElement,
  e: React.MouseEvent<HTMLElement>,
) {
  const target = e.target as Element;
  // The click target may be nested arbitrarily deep inside the anchor
  // (e.g. text with multiple marks), so walk up to the nearest one.
  const href = target.closest('a')?.getAttribute('href') ?? null;
  if (!href) return;

  e.preventDefault();
  e.stopPropagation();

  if (href.indexOf('#') !== 0) {
    window.open(href, '_blank', 'noopener noreferrer');
    return;
  }

  if (href === '#') {
    articleElement.scrollIntoView({
      behavior: 'smooth',
    });
    return;
  }

  try {
    const hrefValue = href.slice(1);
    const nameSelector = `[name="${hrefValue}"]`;
    const idSelector = `[id="${hrefValue}"]`;
    const elemName =
      articleElement.querySelector(nameSelector) ||
      articleElement.querySelector(idSelector);
    if (elemName) {
      elemName.scrollIntoView({ behavior: 'smooth' });
      return;
    }
  } catch {
    // ignoring error
  }

  try {
    const elemId = articleElement.querySelector(href);
    if (elemId) {
      elemId.scrollIntoView({ behavior: 'smooth' });
      return;
    }
  } catch {
    // ignoring error
  }
}
